import type {
  AdminUsageModelFilterOption,
  AdminUsageSeatRow,
  BalanceTransactionEntry,
  UsageRequestLogEntry,
  V1BillingStatement,
} from '@nadoutong/contracts';
import { getStatements, getTransactions } from '../api/billing.ts';
import { PlatformApiError } from '../api/client.ts';
import {
  type AdminUsageDashboardClientQuery,
  getAdminUsageDashboard,
  getUsageRequests,
  type UsageRequestsQuery,
} from '../api/usage.ts';
import { usdMicrosFromCentsFallback } from '../components/formatters.ts';
import { reportExportAuditEvent } from './audit.ts';
import {
  assembleStatementSheet,
  assembleTransactionSheet,
  type StatementCellOverrides,
} from './billing.ts';
import { collectPagedRows } from './collect.ts';
import { exportFilename } from './filename.ts';
import { isValidExportRate } from './money.ts';
import {
  addMicrosAtRate,
  assembleRateSheet,
  CNY_REFERENCE_BLANK_NOTE,
  CONSUMPTION_MODEL_FALLBACK_NOTE,
  type ExportRateBook,
  loadExportRates,
  monthsCoveredByInstantRange,
  monthsInBook,
  monthsInExclusiveDaySpan,
  planRequestMoney,
  planSpanRates,
  rateForModel,
  type ShanghaiMonthSlice,
  serviceMonth,
  shanghaiMonthSlices,
  spanHasSplitRates,
  statementMonthSlices,
  unavailableMonths,
  uniformUsageRate,
  yuanForMicrosByRate,
} from './rates.ts';
import { assembleRequestSheet } from './requests.ts';
import type { ExportResult, ExportStop } from './types.ts';
import {
  assembleUsageSheet,
  indexSeatCostMicros,
  seatCostMicros,
  type UsageSeatMoney,
  usageModelSplitMatches,
} from './usage.ts';
import { collectWindowedRows } from './windows.ts';
import { downloadSheets } from './workbook.ts';

const REQUESTS_PAGE = 200;
const USAGE_PAGE = 100;
const STATEMENTS_PAGE = 120;
const TRANSACTIONS_PAGE = 100;

/**
 * Unfiltered month read plus one read per model. Live spend in the open
 * month can move between those calls, so a failed self-check is retried
 * twice more (three attempts) before the export refuses to download.
 */
const USAGE_SPLIT_ATTEMPTS = 3;

const UTC8_OFFSET_MS = 8 * 60 * 60 * 1000;

/** UTC+8 calendar date — same clock `Billing.tsx` uses for overdue. */
export function todayYmdUtc8(nowMs: number = Date.now()): string {
  return new Date(nowMs + UTC8_OFFSET_MS).toISOString().slice(0, 10);
}

export async function exportRequestLogs(options: {
  readonly fetchImpl: typeof fetch;
  readonly orgId: string;
  readonly query: Omit<UsageRequestsQuery, 'limit' | 'offset'>;
  readonly rangeDays: { readonly from: string; readonly to: string };
  readonly onProgress?: (rows: number) => void;
}): Promise<ExportResult> {
  void reportExportAuditEvent(options.fetchImpl, {
    type: 'export',
    surface: 'requests',
    range: { from: options.query.from ?? null, to: options.query.to ?? null },
  });
  try {
    const collected = await collectRequestRows(options);
    if (collected.status === 'cap') return { status: 'cap' };
    const months = requestCoveredMonths(options.query, collected.rows);
    const first = months[0];
    const last = months[months.length - 1];
    if (first === undefined || last === undefined) {
      await downloadSheets(
        [
          assembleRequestSheet(collected.rows, []),
          assembleRateSheet([], { aggregateAmounts: false }),
        ],
        exportFilename('请求日志', options.rangeDays.from, options.rangeDays.to),
      );
      return { status: 'ok' };
    }
    const book = await loadExportRates(options.fetchImpl, first, last);
    const planned = planRequestMoney(collected.rows, book, months);
    if (planned.status !== 'ok') return planned;
    await downloadSheets(
      [
        assembleRequestSheet(collected.rows, planned.cells),
        assembleRateSheet(monthsInBook(months, book), { aggregateAmounts: false }),
      ],
      exportFilename('请求日志', options.rangeDays.from, options.rangeDays.to),
    );
    return { status: 'ok' };
  } catch {
    return { status: 'error' };
  }
}

export async function exportUsageSeats(options: {
  readonly fetchImpl: typeof fetch;
  readonly orgId: string;
  readonly query: AdminUsageDashboardClientQuery;
  readonly rangeDays: { readonly from: string; readonly to: string };
  readonly onProgress?: (rows: number) => void;
}): Promise<ExportResult> {
  void reportExportAuditEvent(options.fetchImpl, {
    type: 'export',
    surface: 'usage',
    range: { from: options.query.from ?? null, to: options.query.to ?? null },
  });
  try {
    const collected = await collectPagedRows<AdminUsageSeatRow>({
      pageSize: USAGE_PAGE,
      onProgress: options.onProgress,
      fetchPage: async (offset, limit) => {
        const page = await getAdminUsageDashboard(options.fetchImpl, {
          ...options.query,
          limit,
          offset,
        });
        return { rows: page.seats.rows, total: page.seats.total };
      },
    });
    if (collected.status === 'cap') return { status: 'cap' };
    const from = options.query.from;
    const to = options.query.to;
    if (
      typeof from !== 'string' ||
      typeof to !== 'string' ||
      from.length === 0 ||
      to.length === 0
    ) {
      return { status: 'error' };
    }
    const months = monthsCoveredByInstantRange(from, to);
    const first = months[0];
    const last = months[months.length - 1];
    if (first === undefined || last === undefined) return { status: 'error' };
    const book = await loadExportRates(options.fetchImpl, first, last);
    const unavailable = unavailableMonths(months, book);
    if (unavailable.length > 0) return { status: 'rate_unavailable', months: unavailable };
    // Both classes share one rate in every covered month, and the months
    // share that rate: the whole-range seat rows are already the money.
    // No further dashboard read — a 7/7 customer stays on this path.
    const fast = uniformUsageRate(months, book);
    let money: number | UsageSeatMoney;
    if (fast.ok) {
      money = fast.rate;
    } else {
      const priced = await priceUsageSeatsExactly({
        fetchImpl: options.fetchImpl,
        query: options.query,
        rows: collected.rows,
        from,
        to,
        months,
        book,
        fetchedRows: collected.rows.length,
        onProgress: options.onProgress,
      });
      if (priced.status !== 'ready') return priced;
      money = priced.money;
    }
    await downloadUsageWorkbook(options, collected.rows, money, months, book);
    return { status: 'ok' };
  } catch {
    return { status: 'error' };
  }
}

export async function exportBilling(options: {
  readonly fetchImpl: typeof fetch;
  readonly orgId: string;
  readonly onProgress?: (rows: number) => void;
}): Promise<ExportResult> {
  void reportExportAuditEvent(options.fetchImpl, {
    type: 'export',
    surface: 'billing',
    range: { from: null, to: null },
  });
  try {
    const statements = await collectStatements(options.fetchImpl, options.onProgress);
    if (statements.status === 'cap') return { status: 'cap' };
    const transactions = await collectTransactions(options.fetchImpl, (rows) => {
      options.onProgress?.(statements.rows.length + rows);
    });
    if (transactions.status === 'cap') return { status: 'cap' };
    const today = todayYmdUtc8();
    const months = billingCoveredMonths(statements.rows, transactions.rows);
    const first = months[0];
    const last = months[months.length - 1];
    if (first === undefined || last === undefined) {
      await downloadSheets(
        [
          assembleStatementSheet(statements.rows, [], today),
          assembleTransactionSheet(transactions.rows, []),
          assembleRateSheet([], { aggregateAmounts: true }),
        ],
        exportFilename('账单', null, null),
      );
      return { status: 'ok' };
    }
    const book = await loadExportRates(options.fetchImpl, first, last);
    const unavailable = unavailableMonths(months, book);
    if (unavailable.length > 0) return { status: 'rate_unavailable', months: unavailable };
    const statementRates = planSpanRates(
      statements.rows.map((row) => monthsInExclusiveDaySpan(row.periodStart, row.periodEnd)),
      book,
    );
    if (statementRates.status !== 'ok') return statementRates;
    const transactionRates = planSpanRates(
      transactions.rows.map((row) => {
        const month = serviceMonth(row.createdAt);
        if (month === null) throw new Error('transaction has no service month');
        return [month];
      }),
      book,
    );
    if (transactionRates.status !== 'ok') return transactionRates;
    const priced = await priceStatementConsumption(options.fetchImpl, statements.rows, book);
    if (priced.status !== 'ready') return priced;
    const statementSheet =
      priced.overrides === null
        ? assembleStatementSheet(statements.rows, statementRates.rates, today)
        : assembleStatementSheet(statements.rows, statementRates.rates, today, priced.overrides);
    await downloadSheets(
      [
        statementSheet,
        assembleTransactionSheet(transactions.rows, transactionRates.rates),
        assembleRateSheet(monthsInBook(months, book), {
          aggregateAmounts: true,
          extraNotes: priced.notes,
        }),
      ],
      exportFilename('账单', null, null),
    );
    return { status: 'ok' };
  } catch {
    return { status: 'error' };
  }
}

async function downloadUsageWorkbook(
  options: { readonly rangeDays: { readonly from: string; readonly to: string } },
  rows: readonly AdminUsageSeatRow[],
  money: number | UsageSeatMoney,
  months: readonly string[],
  book: ExportRateBook,
): Promise<void> {
  await downloadSheets(
    [
      assembleUsageSheet(rows, money),
      // Each month (and, when its two rates differ, each model) is priced
      // on its own. The aggregate-rate caveat is for sheets that cannot.
      assembleRateSheet(monthsInBook(months, book), { aggregateAmounts: false }),
    ],
    exportFilename('用量', options.rangeDays.from, options.rangeDays.to),
  );
}

type UsagePriceResult =
  | { readonly status: 'ready'; readonly money: UsageSeatMoney }
  | { readonly status: 'cap' }
  | { readonly status: 'error' }
  | Extract<ExportStop, { status: 'unknown_models' }>;

async function priceUsageSeatsExactly(input: {
  readonly fetchImpl: typeof fetch;
  readonly query: AdminUsageDashboardClientQuery;
  readonly rows: readonly AdminUsageSeatRow[];
  readonly from: string;
  readonly to: string;
  readonly months: readonly string[];
  readonly book: ExportRateBook;
  readonly fetchedRows: number;
  readonly onProgress?: (rows: number) => void;
}): Promise<UsagePriceResult> {
  const slices = shanghaiMonthSlices(input.from, input.to);
  const lastMonth = input.months[input.months.length - 1];
  const limitCandidate =
    lastMonth === undefined
      ? null
      : (input.book.byMonth.get(lastMonth)?.usdPricedCnyPerUsd ?? null);
  // 月度上限 is a USD budget on the seat, not settled usage. Convert it
  // at the last covered month's USD-priced rate.
  if (slices.length === 0 || !isValidExportRate(limitCandidate)) return { status: 'error' };
  const limitRate = limitCandidate;

  let fetchedRows = input.fetchedRows;
  const noteRows = (pageRows: number): void => {
    input.onProgress?.(fetchedRows + pageRows);
  };
  const commitRows = (count: number): void => {
    fetchedRows += count;
  };

  const buckets = new Map<string, Map<number, number>>();
  const seen = new Set<string>();
  const unknown: string[] = [];

  for (const slice of slices) {
    const entry = input.book.byMonth.get(slice.month);
    if (entry === undefined) return { status: 'error' };
    const usdRate = entry.usdPricedCnyPerUsd;
    const cnyRate = entry.cnyPricedCnyPerUsd;
    if (!isValidExportRate(usdRate) || !isValidExportRate(cnyRate)) return { status: 'error' };
    const sliceQuery = { ...input.query, from: slice.from, to: slice.to };
    if (usdRate === cnyRate) {
      const loaded = await readUsageSlice(input.fetchImpl, sliceQuery, noteRows);
      if (loaded.status !== 'ok') return { status: 'cap' };
      commitRows(loaded.rowCount);
      for (const [id, micros] of loaded.seats) {
        seen.add(id);
        addSeatMicros(buckets, id, usdRate, micros);
      }
      continue;
    }

    let settled = false;
    for (let attempt = 0; attempt < USAGE_SPLIT_ATTEMPTS; attempt += 1) {
      const loaded = await readSplitMonth(
        input.fetchImpl,
        input.book,
        entry,
        sliceQuery,
        slice,
        noteRows,
        commitRows,
      );
      if (loaded.status === 'cap') return { status: 'cap' };
      if (!loaded.matches) continue;
      if (loaded.unknown.length > 0) unknown.push(...loaded.unknown);
      else {
        for (const id of loaded.seenIds) seen.add(id);
        mergeSeatBuckets(buckets, loaded.buckets);
      }
      settled = true;
      break;
    }
    if (!settled) return { status: 'error' };
  }

  if (unknown.length > 0) return { status: 'unknown_models', models: dedupeLabels(unknown) };

  const yuanByMember = new Map<string, number>();
  for (const row of input.rows) {
    if (!seen.has(row.memberAccountId)) {
      if (seatCostMicros(row) !== 0) return { status: 'error' };
      yuanByMember.set(row.memberAccountId, 0);
      continue;
    }
    const byRate = buckets.get(row.memberAccountId);
    yuanByMember.set(row.memberAccountId, byRate === undefined ? 0 : yuanForMicrosByRate(byRate));
  }
  return { status: 'ready', money: { yuanByMember, limitRate } };
}

type SliceRead =
  | { readonly status: 'cap' }
  | {
      readonly status: 'ok';
      readonly seats: ReadonlyMap<string, number>;
      readonly summaryMicros: number;
      readonly filterModels: readonly AdminUsageModelFilterOption[];
      readonly rowCount: number;
    };

async function readUsageSlice(
  fetchImpl: typeof fetch,
  query: AdminUsageDashboardClientQuery,
  noteRows: (rows: number) => void,
): Promise<SliceRead> {
  const captured: { page: Awaited<ReturnType<typeof getAdminUsageDashboard>> | null } = {
    page: null,
  };
  const collected = await collectPagedRows<AdminUsageSeatRow>({
    pageSize: USAGE_PAGE,
    onProgress: noteRows,
    fetchPage: async (offset, limit) => {
      const page = await getAdminUsageDashboard(fetchImpl, { ...query, limit, offset });
      if (captured.page === null) captured.page = page;
      return { rows: page.seats.rows, total: page.seats.total };
    },
  });
  if (collected.status === 'cap') return { status: 'cap' };
  const page = captured.page;
  if (page === null) return { status: 'cap' };
  return {
    status: 'ok',
    seats: indexSeatCostMicros(collected.rows),
    summaryMicros: usdMicrosFromCentsFallback(
      page.summary.totalCostUsdMicros,
      page.summary.totalCostUsdCents,
    ),
    filterModels: page.filters.models,
    rowCount: collected.rows.length,
  };
}

type SplitRead =
  | { readonly status: 'cap' }
  | {
      readonly status: 'ok';
      readonly matches: boolean;
      readonly seenIds: ReadonlySet<string>;
      readonly buckets: ReadonlyMap<string, ReadonlyMap<number, number>>;
      readonly unknown: readonly string[];
    };

async function readSplitMonth(
  fetchImpl: typeof fetch,
  book: ExportRateBook,
  entry: ExportRateBook['months'][number],
  query: AdminUsageDashboardClientQuery,
  slice: ShanghaiMonthSlice,
  noteRows: (rows: number) => void,
  commitRows: (count: number) => void,
): Promise<SplitRead> {
  const unfiltered = await readUsageSlice(fetchImpl, query, noteRows);
  if (unfiltered.status !== 'ok') return { status: 'cap' };
  commitRows(unfiltered.rowCount);

  const modelSummaries: number[] = [];
  const modelSeats: ReadonlyMap<string, number>[] = [];
  const options = selectedModelOptions(unfiltered.filterModels, query);
  for (const option of options) {
    // Structured `model` + `vendor` only. `option.id` is a display key.
    const loaded = await readUsageSlice(
      fetchImpl,
      { ...query, from: slice.from, to: slice.to, model: option.model, vendor: option.vendor },
      noteRows,
    );
    if (loaded.status !== 'ok') return { status: 'cap' };
    commitRows(loaded.rowCount);
    modelSummaries.push(loaded.summaryMicros);
    modelSeats.push(loaded.seats);
  }

  const matches = usageModelSplitMatches({
    unfilteredSummaryMicros: unfiltered.summaryMicros,
    unfilteredSeats: unfiltered.seats,
    modelSummaryMicros: modelSummaries,
    modelSeats,
  });
  const seenIds = new Set<string>(unfiltered.seats.keys());
  if (!matches) {
    return { status: 'ok', matches: false, seenIds, buckets: new Map(), unknown: [] };
  }

  const buckets = new Map<string, Map<number, number>>();
  const unknown: string[] = [];
  for (let index = 0; index < options.length; index += 1) {
    const option = options[index];
    const seats = modelSeats[index];
    if (option === undefined || seats === undefined) continue;
    if (microsTotal(seats) === 0) continue;
    const picked = rateForModel(entry, book, option.vendor, option.model);
    if (!picked.ok) {
      unknown.push(`${option.vendor}/${option.model}`);
      continue;
    }
    for (const [id, micros] of seats) addSeatMicros(buckets, id, picked.rate, micros);
  }
  return { status: 'ok', matches: true, seenIds, buckets, unknown };
}

function selectedModelOptions(
  options: readonly AdminUsageModelFilterOption[],
  query: AdminUsageDashboardClientQuery,
): readonly AdminUsageModelFilterOption[] {
  const model = query.model;
  if (typeof model !== 'string' || model.length === 0) return options;
  const vendor = query.vendor;
  const vendorSet = typeof vendor === 'string' && vendor.length > 0;
  return options.filter((option) => {
    if (option.model !== model) return false;
    if (vendorSet && option.vendor !== vendor) return false;
    return true;
  });
}

function addSeatMicros(
  buckets: Map<string, Map<number, number>>,
  memberAccountId: string,
  rate: number,
  micros: number,
): void {
  if (micros === 0) return;
  let byRate = buckets.get(memberAccountId);
  if (byRate === undefined) {
    byRate = new Map();
    buckets.set(memberAccountId, byRate);
  }
  addMicrosAtRate(byRate, rate, micros);
}

function mergeSeatBuckets(
  target: Map<string, Map<number, number>>,
  source: ReadonlyMap<string, ReadonlyMap<number, number>>,
): void {
  for (const [id, byRate] of source) {
    for (const [rate, micros] of byRate) addSeatMicros(target, id, rate, micros);
  }
}

function microsTotal(seats: ReadonlyMap<string, number>): number {
  let total = 0;
  for (const micros of seats.values()) total += micros;
  return total;
}

type StatementPriceResult =
  | {
      readonly status: 'ready';
      readonly overrides: StatementCellOverrides | null;
      readonly notes: ReadonlyMap<string, readonly string[]> | undefined;
    }
  | Extract<ExportStop, { status: 'unknown_models' }>;

/**
 * 人民币参考期末应收 is blanked wherever a statement touches a month
 * whose class rates differ. 用量 is rebuilt from per-model dashboard
 * totals for those statements only; a 7/7 statement is left alone.
 */
async function priceStatementConsumption(
  fetchImpl: typeof fetch,
  statements: readonly V1BillingStatement[],
  book: ExportRateBook,
): Promise<StatementPriceResult> {
  const blankCnyReference: boolean[] = [];
  const referenceMonths = new Set<string>();
  let anySplit = false;
  for (const row of statements) {
    const span = monthsInExclusiveDaySpan(row.periodStart, row.periodEnd);
    const split = spanHasSplitRates(span, book);
    blankCnyReference.push(split);
    if (!split) continue;
    anySplit = true;
    for (const month of span) {
      const entry = book.byMonth.get(month);
      if (entry !== undefined && entry.usdPricedCnyPerUsd !== entry.cnyPricedCnyPerUsd) {
        referenceMonths.add(month);
      }
    }
  }
  if (!anySplit) return { status: 'ready', overrides: null, notes: undefined };

  const consumptionYuan: (number | undefined)[] = statements.map(() => undefined);
  const fallbackMonths = new Set<string>();
  const unknown: string[] = [];
  for (let index = 0; index < statements.length; index += 1) {
    const row = statements[index];
    if (row === undefined || blankCnyReference[index] !== true) continue;
    const span = monthsInExclusiveDaySpan(row.periodStart, row.periodEnd);
    const rebuilt = await rebuildStatementUsage(fetchImpl, row, book);
    if (rebuilt.status === 'fallback') {
      for (const month of span) fallbackMonths.add(month);
      continue;
    }
    if (rebuilt.status === 'unknown_models') {
      unknown.push(...rebuilt.models);
      continue;
    }
    consumptionYuan[index] = rebuilt.yuan;
  }
  if (unknown.length > 0) return { status: 'unknown_models', models: dedupeLabels(unknown) };
  return {
    status: 'ready',
    overrides: { consumptionYuan, blankCnyReference },
    notes: rateNotes(referenceMonths, fallbackMonths),
  };
}

type StatementUsageRebuild =
  | { readonly status: 'yuan'; readonly yuan: number }
  | { readonly status: 'fallback' }
  | { readonly status: 'unknown_models'; readonly models: readonly string[] };

async function rebuildStatementUsage(
  fetchImpl: typeof fetch,
  statement: V1BillingStatement,
  book: ExportRateBook,
): Promise<StatementUsageRebuild> {
  const slices = statementMonthSlices(statement.periodStart, statement.periodEnd);
  let micros = 0;
  const byRate = new Map<number, number>();
  const unknown: string[] = [];
  try {
    for (const slice of slices) {
      const page = await getAdminUsageDashboard(fetchImpl, {
        from: slice.from,
        to: slice.to,
        limit: 1,
      });
      const entry = book.byMonth.get(slice.month);
      if (entry === undefined || !Array.isArray(page.models)) return { status: 'fallback' };
      for (const model of page.models) {
        const modelMicros = usdMicrosFromCentsFallback(model.costUsdMicros, model.costUsdCents);
        micros += modelMicros;
        if (modelMicros === 0) continue;
        const picked = rateForModel(entry, book, model.vendor, model.model);
        if (!picked.ok) {
          unknown.push(`${model.vendor}/${model.model}`);
          continue;
        }
        addMicrosAtRate(byRate, picked.rate, modelMicros);
      }
    }
  } catch {
    return { status: 'fallback' };
  }
  if (micros !== statement.consumptionUsdMicros) return { status: 'fallback' };
  if (unknown.length > 0) return { status: 'unknown_models', models: dedupeLabels(unknown) };
  return { status: 'yuan', yuan: yuanForMicrosByRate(byRate) };
}

function rateNotes(
  referenceMonths: ReadonlySet<string>,
  fallbackMonths: ReadonlySet<string>,
): ReadonlyMap<string, readonly string[]> | undefined {
  const months = new Set<string>([...referenceMonths, ...fallbackMonths]);
  if (months.size === 0) return undefined;
  const notes = new Map<string, readonly string[]>();
  for (const month of months) {
    const lines: string[] = [];
    if (referenceMonths.has(month)) lines.push(CNY_REFERENCE_BLANK_NOTE);
    if (fallbackMonths.has(month)) lines.push(CONSUMPTION_MODEL_FALLBACK_NOTE);
    if (lines.length > 0) notes.set(month, lines);
  }
  return notes.size > 0 ? notes : undefined;
}

function dedupeLabels(labels: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const label of labels) {
    if (seen.has(label)) continue;
    seen.add(label);
    out.push(label);
  }
  return out;
}

async function collectRequestRows(options: {
  readonly fetchImpl: typeof fetch;
  readonly query: Omit<UsageRequestsQuery, 'limit' | 'offset'>;
  readonly onProgress?: (rows: number) => void;
}): Promise<
  | { readonly status: 'ok'; readonly rows: readonly UsageRequestLogEntry[] }
  | { readonly status: 'cap' }
> {
  const fetchPage = async (
    from: string | undefined,
    to: string | undefined,
    offset: number,
    limit: number,
  ) => {
    const page = await getUsageRequests(options.fetchImpl, {
      ...options.query,
      ...(from === undefined ? {} : { from }),
      ...(to === undefined ? {} : { to }),
      limit,
      offset,
      // Spreading the page query would inherit `detail: 'summary'`
      // and silently drop reasoning / image / unit-billing columns
      // on archived rows. Export always hydrates.
      detail: 'full',
    });
    return { rows: page.rows, total: page.total };
  };
  const requestId = options.query.requestId;
  if (typeof requestId === 'string' && requestId.length > 0) {
    return collectPagedRows<UsageRequestLogEntry>({
      pageSize: REQUESTS_PAGE,
      onProgress: options.onProgress,
      fetchPage: async (offset, limit) =>
        fetchPage(options.query.from, options.query.to, offset, limit),
    });
  }
  const from = options.query.from;
  const to = options.query.to;
  if (typeof from !== 'string' || from.length === 0 || typeof to !== 'string' || to.length === 0) {
    return collectPagedRows<UsageRequestLogEntry>({
      pageSize: REQUESTS_PAGE,
      onProgress: options.onProgress,
      fetchPage: async (offset, limit) => fetchPage(undefined, undefined, offset, limit),
    });
  }
  return collectWindowedRows<UsageRequestLogEntry>({
    fromIso: from,
    toIso: to,
    pageSize: REQUESTS_PAGE,
    onProgress: options.onProgress,
    fetchPage: async (query) => fetchPage(query.from, query.to, query.offset, query.limit),
  });
}

/**
 * Months the request-log export has to price. A single-request lookup
 * ignores the picker window (the gateway does too) and uses the row.
 * A range export uses the half-open picker window plus any row month,
 * so a row that lands outside the window is still priced.
 */
function requestCoveredMonths(
  query: Omit<UsageRequestsQuery, 'limit' | 'offset'>,
  rows: readonly UsageRequestLogEntry[],
): readonly string[] {
  const found = new Set<string>();
  const single = typeof query.requestId === 'string' && query.requestId.length > 0;
  if (!single && typeof query.from === 'string' && typeof query.to === 'string') {
    for (const month of monthsCoveredByInstantRange(query.from, query.to)) found.add(month);
  }
  for (const row of rows) {
    const month = serviceMonth(row.createdAt);
    if (month !== null) found.add(month);
  }
  return [...found].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
}

function billingCoveredMonths(
  statements: readonly V1BillingStatement[],
  transactions: readonly BalanceTransactionEntry[],
): readonly string[] {
  const found = new Set<string>();
  for (const row of statements) {
    for (const month of monthsInExclusiveDaySpan(row.periodStart, row.periodEnd)) found.add(month);
  }
  for (const row of transactions) {
    const month = serviceMonth(row.createdAt);
    if (month !== null) found.add(month);
  }
  return [...found].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
}

async function collectStatements(
  fetchImpl: typeof fetch,
  onProgress?: (rows: number) => void,
): Promise<
  | { readonly status: 'ok'; readonly rows: readonly V1BillingStatement[] }
  | { readonly status: 'cap' }
> {
  try {
    return await collectPagedRows<V1BillingStatement>({
      pageSize: STATEMENTS_PAGE,
      onProgress,
      fetchPage: async (offset, limit) => {
        const page = await getStatements(fetchImpl, { limit, offset });
        return { rows: page.statements, total: null };
      },
    });
  } catch (error) {
    if (error instanceof PlatformApiError && error.status === 403) {
      return { status: 'ok', rows: [] };
    }
    throw error;
  }
}

async function collectTransactions(
  fetchImpl: typeof fetch,
  onProgress?: (rows: number) => void,
): Promise<
  | { readonly status: 'ok'; readonly rows: readonly BalanceTransactionEntry[] }
  | { readonly status: 'cap' }
> {
  try {
    return await collectPagedRows<BalanceTransactionEntry>({
      pageSize: TRANSACTIONS_PAGE,
      onProgress,
      fetchPage: async (offset, limit) => {
        const page = await getTransactions(fetchImpl, { limit, offset });
        return { rows: page.transactions, total: null };
      },
    });
  } catch (error) {
    if (error instanceof PlatformApiError && error.status === 403) {
      return { status: 'ok', rows: [] };
    }
    throw error;
  }
}
